import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarPlus,
  CheckCircle2,
  Clock,
  MessageCircle,
  PhoneCall,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { loadFollowUpDailyQueue, recordFollowUpContact } from './data.js';
import { dateLabel, friendlyError, number } from './lib/format.js';

const STATUS_META = {
  BELUM: { label: 'Belum di-FU', className: 'fu-status-neutral' },
  PROSES: { label: 'Proses FU', className: 'fu-status-process' },
  DONE: { label: 'Sudah di-FU', className: 'fu-status-done' },
  FAILED: { label: 'Gagal / Tidak Merespon', className: 'fu-status-failed' },
  COOLDOWN: { label: 'Proteksi Spam', className: 'fu-status-cooldown' },
};

const RESULT_OPTIONS = [
  'Tertarik',
  'Menolak',
  'Minta dihubungi minggu depan',
  'Beli ulang',
  'Tidak merespon',
];

const METHOD_OPTIONS = ['WHATSAPP', 'TELEPON', 'EMAIL'];

function formatWaPhone(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('62')) return digits;
  if (digits.startsWith('0')) return `62${digits.slice(1)}`;
  return `62${digits}`;
}

function getSalutation(client) {
  const name = client?.name || '';
  const gender = (client?.gender || '').toLowerCase();
  if (/perempuan/i.test(gender)) return `Ibu ${name}`;
  if (/laki/i.test(gender)) return `Bapak ${name}`;
  return name || 'Bapak/Ibu';
}

function buildWaMessage(row, clinicName) {
  const parts = [
    `Halo ${getSalutation(row.client)}, kami dari ${clinicName || 'klinik XV'}.`,
    '',
    `Kami ingin follow-up kunjungan Anda pada ${dateLabel(row.visit_date)}.`,
  ];
  if (row.current_complaint) parts.push(`Keluhan saat itu: ${row.current_complaint}`);
  if (row.action_notes) parts.push(`Catatan nakes: ${row.action_notes}`);
  parts.push('', 'Bila berkenan, kami dapat bantu jadwalkan kunjungan lanjutan. Terima kasih.');
  return parts.join('\n');
}

function FollowUpLogModal({ row, defaultStatus, identity, onClose, onSaved }) {
  const [method, setMethod] = useState('WHATSAPP');
  const [resultStatus, setResultStatus] = useState(defaultStatus === 'FAILED' ? 'Tidak merespon' : 'Tertarik');
  const [responseText, setResponseText] = useState('');
  const [nextActionDate, setNextActionDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit() {
    setBusy(true);
    setError('');
    try {
      await recordFollowUpContact({
        visitId: row.id,
        clientId: row.client_id,
        contactedBy: identity?.appUser?.email || identity?.appUser?.name || null,
        method,
        resultStatus,
        responseText,
        nextActionDate,
        messageText: [
          `Status: ${defaultStatus === 'FAILED' ? 'Gagal / Tidak Merespon' : 'Sudah di-FU'}`,
          `Hasil: ${resultStatus}`,
          responseText ? `Respon: ${responseText}` : '',
          nextActionDate ? `Next action: ${nextActionDate}` : '',
        ].filter(Boolean).join('\n'),
      });
      onSaved(defaultStatus, { method, resultStatus, responseText, nextActionDate });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card fu-log-modal" role="dialog" aria-modal="true" aria-label="Log follow-up">
        <header className="modal-head">
          <div className="modal-title-icon"><PhoneCall size={22} /></div>
          <div className="grow">
            <h2>Log Follow-Up</h2>
            <p>{row.client?.name} · {row.bucket_label}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Tutup">×</button>
        </header>
        <div className="modal-body">
          {error ? <div className="alert alert-danger">{error}</div> : null}
          <div className="form-grid">
            <label className="form-field">
              <span>Kanal kontak</span>
              <select value={method} onChange={(event) => setMethod(event.target.value)}>
                {METHOD_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label className="form-field">
              <span>Hasil / respon klien</span>
              <select value={resultStatus} onChange={(event) => setResultStatus(event.target.value)}>
                {RESULT_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label className="form-field form-field-wide">
              <span>Catatan respon</span>
              <textarea value={responseText} onChange={(event) => setResponseText(event.target.value)} rows="3" placeholder="Contoh: tertarik paket, minta dihubungi Jumat, menolak promo." />
            </label>
            <label className="form-field">
              <span>Next action date</span>
              <input type="date" value={nextActionDate} onChange={(event) => setNextActionDate(event.target.value)} />
            </label>
          </div>
        </div>
        <footer className="modal-actions">
          <button className="button button-quiet" onClick={onClose} disabled={busy}>Batal</button>
          <button className="button button-primary" onClick={submit} disabled={busy}>
            {busy ? 'Menyimpan...' : 'Simpan Log'}
          </button>
        </footer>
      </section>
    </div>
  );
}

export function FollowUpPage({ identity, onGoToReservasi }) {
  const [queue, setQueue] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  const roleName = identity?.role?.role_name || '';
  const isMarketing = /(marketing|frontliner)/i.test(roleName);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await loadFollowUpDailyQueue({
        cooldownDays: 7,
        marketerUserId: isMarketing ? identity?.appUser?.id : null,
      });
      setQueue(data);
      setRows(data.rows || []);
      setStatusMap(Object.fromEntries((data.rows || []).map((row) => [row.client_id, row.fu_status || 'BELUM'])));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [identity?.appUser?.id, isMarketing]);

  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row.client?.name, row.client?.phone, row.client?.client_code, row.bucket_label, row.current_complaint, row.action_notes]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [rows, search]);

  const stats = useMemo(() => {
    const total = rows.length || 20;
    const done = rows.filter((row) => ['DONE', 'FAILED'].includes(statusMap[row.client_id])).length;
    const processing = rows.filter((row) => statusMap[row.client_id] === 'PROSES').length;
    return {
      done,
      processing,
      remaining: Math.max(0, rows.length - done),
      completion: rows.length ? Math.round((done / rows.length) * 100) : 0,
      targetCompletion: total ? Math.round((done / total) * 100) : 0,
    };
  }, [rows, statusMap]);

  function setRowStatus(row, status) {
    setStatusMap((current) => ({ ...current, [row.client_id]: status }));
  }

  function handleWaClick(row) {
    const phone = formatWaPhone(row.client?.phone);
    if (!phone) {
      alert(`Nomor telepon ${row.client?.name || 'pasien'} tidak tersedia.`);
      return;
    }
    if (row.cooldown_locked) {
      alert(`Klien ${row.client?.name} baru di-FU pada ${dateLabel(row.last_contact?.contacted_at)}. Harap tunggu hingga ${dateLabel(row.cooldown_until)}.`);
      return;
    }
    const message = buildWaMessage(row, identity?.clinic?.name);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
    setRowStatus(row, 'PROSES');
  }

  function handleLogSaved(row, status) {
    setRowStatus(row, status);
    setRows((current) => current.map((item) => (
      item.client_id === row.client_id
        ? {
            ...item,
            fu_status: status,
            last_contact: {
              contacted_at: new Date().toISOString(),
              contacted_by: identity?.appUser?.name || 'Staff',
              method: 'WHATSAPP',
            },
            cooldown_until: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
          }
        : item
    )));
    setDialog(null);
  }

  const grouped = {
    newWarm: filteredRows.filter((row) => row.bucket === 'newWarm'),
    routine: filteredRows.filter((row) => row.bucket === 'routine'),
    dormant: filteredRows.filter((row) => row.bucket === 'dormant'),
  };

  return (
    <div className="page-stack">
      <article className="panel" style={{ padding: '24px' }}>
        <div className="panel-head">
          <div>
            <span className="eyebrow">Daily FU Queue</span>
            <h3>Follow-Up Harian 20 Klien</h3>
            <p className="muted">Kuota 7 klien baru/hangat, 7 rutin/mingguan, dan 6 dormant. Cooldown kontak ulang 7 hari.</p>
            {isMarketing ? <p className="muted fu-scope-note">Menampilkan klien aktif yang terhubung ke akun Marketing Anda.</p> : null}
          </div>
          <button className="button button-quiet" onClick={load} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
        <div className="fu-control-row">
          <label className="search-box fu-search-box">
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cari nama, telepon, kategori, catatan..." />
          </label>
        </div>
        {error ? <div className="alert alert-danger">{error}</div> : null}
      </article>

      <section className="fu-metrics">
        <article><span>Target Hari Ini</span><strong>{number.format(rows.length)} / 20</strong><small>Antrean bebas cooldown</small></article>
        <article><span>Selesai / Gagal</span><strong>{number.format(stats.done)}</strong><small>{stats.completion}% dari antrean</small></article>
        <article><span>Proses FU</span><strong>{number.format(stats.processing)}</strong><small>Sedang dihubungi staf</small></article>
        <article><span>Cooldown</span><strong>{number.format(queue?.summary?.locked || 0)}</strong><small>Proteksi 7 hari</small></article>
      </section>

      <article className="panel fu-policy-panel">
        <ShieldAlert size={18} />
        <div>
          <strong>Aturan anti-spam aktif</strong>
          <span>Klien yang sudah di-FU tidak masuk antrean selama 7 hari. Jika nomor sama muncul kembali, staf wajib menunggu sampai tanggal buka proteksi.</span>
        </div>
      </article>

      {loading ? (
        <div className="content-loading"><div className="loader" /><span>Memuat antrean follow-up...</span></div>
      ) : !filteredRows.length ? (
        <article className="panel" style={{ padding: '24px' }}>
          <div className="empty">
            <CalendarPlus size={32} />
            <p>{search.trim()
              ? 'Tidak ada klien FU yang cocok dengan pencarian ini.'
              : isMarketing
                ? `Belum ada klien Anda yang masuk jendela FU. ${queue?.assignedClientCount || 0} klien aktif hanya muncul pada H+1–3, H+7–14, atau setelah lebih dari 30 hari, dan tidak saat masih memiliki kunjungan aktif.`
                : 'Tidak ada klien FU yang bebas cooldown untuk filter ini.'}</p>
            {search.trim() ? <button type="button" className="button button-quiet" onClick={() => setSearch('')}>Hapus pencarian</button> : null}
          </div>
        </article>
      ) : (
        <>
          <FollowUpGroup title="Klien Baru / Hangat" range="H+1 s.d. H+3" quota={queue?.quotas?.newWarm || 7} rows={grouped.newWarm} statusMap={statusMap} onWaClick={handleWaClick} onStatus={setRowStatus} onLog={setDialog} onGoToReservasi={onGoToReservasi} />
          <FollowUpGroup offset title="Klien Rutin / Mingguan" range="H+7 s.d. H+14" quota={queue?.quotas?.routine || 7} rows={grouped.routine} statusMap={statusMap} onWaClick={handleWaClick} onStatus={setRowStatus} onLog={setDialog} onGoToReservasi={onGoToReservasi} />
          <FollowUpGroup title="Klien Lama / Dormant" range=">30 hari" quota={queue?.quotas?.dormant || 6} rows={grouped.dormant} statusMap={statusMap} onWaClick={handleWaClick} onStatus={setRowStatus} onLog={setDialog} onGoToReservasi={onGoToReservasi} />
        </>
      )}

      {dialog ? (
        <FollowUpLogModal
          row={dialog.row}
          defaultStatus={dialog.status}
          identity={identity}
          onClose={() => setDialog(null)}
          onSaved={(status) => handleLogSaved(dialog.row, status)}
        />
      ) : null}

      <style>{`
        .fu-control-row { display: flex; gap: 12px; margin-top: 12px; }
        .fu-search-box { max-width: 520px; width: 100%; }
        .fu-scope-note { margin-top: 4px; color: var(--green-700); }
        .fu-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
        .fu-metrics article { min-height: 72px; padding: 10px 16px; border: 1px solid var(--border); border-radius: 8px; background: white; }
        .fu-metrics span, .fu-metrics small { display: block; color: var(--muted); font-size: 12px; }
        .fu-metrics strong { display: block; margin: 2px 0; color: var(--green-900); font-size: 22px; line-height: 1.05; }
        .fu-policy-panel { display: flex; align-items: flex-start; gap: 10px; background: #fff8e7; color: #7a4b00; }
        .fu-policy-panel strong, .fu-policy-panel span { display: block; }
        .fu-policy-panel span { margin-top: 2px; font-size: 13px; line-height: 1.45; }
        .fu-group-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
        .fu-group-head h3 { margin: 0; font-size: 16px; }
        .fu-group-head small { display: block; color: var(--muted); margin-top: 2px; }
        .fu-quota-pill { padding: 5px 10px; border-radius: 999px; background: #eef7f1; color: var(--green-900); font-size: 12px; font-weight: 800; }
        .fu-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .fu-table th { text-align: left; padding: 10px; border-bottom: 1px solid var(--border); color: var(--muted); font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
        .fu-table td { padding: 10px; border-bottom: 1px solid #f0ebe2; vertical-align: top; }
        .fu-primary strong, .fu-primary span, .fu-note span { display: block; }
        .fu-primary span, .fu-note span { color: var(--muted); font-size: 12px; margin-top: 2px; }
        .fu-note { max-width: 340px; line-height: 1.45; }
        .fu-status { display: inline-flex; align-items: center; min-height: 26px; padding: 0 9px; border-radius: 999px; font-size: 12px; font-weight: 800; white-space: nowrap; }
        .fu-status-neutral { background: #eeeae3; color: #62665f; }
        .fu-status-process { background: #e8f0ff; color: #2452a6; }
        .fu-status-done { background: #e1f3e9; color: #276a4f; }
        .fu-status-failed { background: #fff0d7; color: #8a5b19; }
        .fu-status-cooldown { background: #fbe5e3; color: #963838; }
        .fu-actions { display: flex; gap: 6px; flex-wrap: wrap; }
        .fu-group-offset { transform: translate(12px, 12px); width: calc(100% - 12px); }
        .fu-log-modal { max-width: 760px; }
        @media (max-width: 900px) {
          .fu-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .fu-table { min-width: 880px; }
        }
      `}</style>
    </div>
  );
}

function FollowUpGroup({ title, range, quota, rows, statusMap, onWaClick, onStatus, onLog, onGoToReservasi, offset = false }) {
  return (
    <article className={`panel table-panel ${offset ? 'fu-group-offset' : ''}`} style={{ padding: '24px' }}>
      <div className="fu-group-head">
        <div>
          <h3>{title}</h3>
          <small>Target FU {range}</small>
        </div>
        <span className="fu-quota-pill">{rows.length} / {quota}</span>
      </div>
      {!rows.length ? (
        <div className="empty"><p>Belum ada klien dalam kategori ini.</p></div>
      ) : (
        <div className="table-wrap">
          <table className="fu-table">
            <thead>
              <tr>
                <th>Klien</th>
                <th>Kunjungan</th>
                <th>Status FU</th>
                <th>Catatan</th>
                <th>Proteksi</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const status = statusMap[row.client_id] || 'BELUM';
                const statusMeta = STATUS_META[status] || STATUS_META.BELUM;
                return (
                  <tr key={`${row.bucket}-${row.client_id}`}>
                    <td className="fu-primary">
                      <strong>{row.client?.name || '-'}</strong>
                      <span>{row.client?.client_code || '-'} · {row.client?.phone || 'No HP kosong'}</span>
                    </td>
                    <td className="fu-primary">
                      <strong>{row.visit_number || '-'}</strong>
                      <span>{dateLabel(row.visit_date)} · H+{row.days_since_visit}</span>
                    </td>
                    <td><span className={`fu-status ${statusMeta.className}`}>{statusMeta.label}</span></td>
                    <td className="fu-note">
                      <strong>{row.note_type}</strong>
                      <span>{row.action_notes || row.current_complaint || 'Tidak ada catatan khusus.'}</span>
                    </td>
                    <td className="fu-note">
                      {row.last_contact ? (
                        <>
                          <strong>FU terakhir {dateLabel(row.last_contact.contacted_at, { time: true })}</strong>
                          <span>Buka proteksi {dateLabel(row.cooldown_until)}</span>
                        </>
                      ) : (
                        <span>Aman dihubungi.</span>
                      )}
                    </td>
                    <td>
                      <div className="fu-actions">
                        <button className="button button-mini button-wa" onClick={() => onWaClick(row)} disabled={!row.client?.phone || status === 'DONE'}>
                          <MessageCircle size={13} /> WA
                        </button>
                        <button className="button button-mini button-soft" onClick={() => onStatus(row, 'PROSES')} disabled={status === 'DONE'}>
                          <Clock size={13} /> Proses
                        </button>
                        <button className="button button-mini button-primary" onClick={() => onLog({ row, status: 'DONE' })}>
                          <CheckCircle2 size={13} /> Selesai
                        </button>
                        <button className="button button-mini button-quiet" onClick={() => onLog({ row, status: 'FAILED' })}>
                          <XCircle size={13} /> Gagal
                        </button>
                        <button className="button button-mini button-quiet" onClick={() => onGoToReservasi?.(row.client)}>
                          <CalendarPlus size={13} /> Jadwalkan
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}
